import React, { useMemo, useState, memo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  useTheme,
  Theme,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';

import { VpBorderRadius } from './styles';
import { BaseQuestion, FormState, QuestionType } from './types';
import AppCard from '../../components/varietyPlanner/AppCard';
import Question from '../../components/varietyPlanner/form/Question';
import { useI18n } from '../../../hooks/useI18n';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';

const makeStyles = (theme: Theme) => ({
  sectionContainer: {
    padding: theme.spacing(1.75, 1.75, 1, 1.75),
  },
  sectionTitleContainer: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
  },
  questionsToggleIconBtn: {
    color: theme.palette.questionsForm.formBlue,
    '&:hover': {
      color: 'questionsForm.formHoverState',
    },
    padding: '9px 0px 0px 0px',
  },
  cleanUpBtn: {
    borderRadius: VpBorderRadius.XLARGE,
    padding: theme.spacing(1),
    width: '144px',
    height: '36px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginBottom: 0,
    },
    backgroundColor: theme.palette.questionsForm.formBlue,
    color: theme.palette.appWhite,
    '&:hover': {
      backgroundColor: theme.palette.questionsForm.formHoverState,
    },
  },
});

interface VpFormProps {
  formState: FormState;
  questions: BaseQuestion[];
  onFormChange: (questionId: string, value: string | string[]) => void;
  onResetForm: () => void;
}

const VpForm: React.FC<Readonly<VpFormProps>> = ({
  formState,
  questions,
  onFormChange,
  onResetForm,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const theme = useTheme();
  const styles = makeStyles(theme);
  const { t } = useI18n();

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const slicedQuestions = !isExpanded ? questions.slice(0, 4) : questions;

  const simpleQuestions = useMemo(
    () =>
      slicedQuestions.filter(
        (question) => question.type !== QuestionType.MultipleSelect
      ),
    [slicedQuestions]
  );

  const multipleChoiceQuestions = useMemo(
    () =>
      slicedQuestions.filter(
        (question) => question.type === QuestionType.MultipleSelect
      ),
    [slicedQuestions]
  );

  return (
    <Box sx={styles.sectionContainer}>
      <Box sx={styles.sectionTitleContainer}>
        <Box>
          <Typography
            component="p"
            variant="appBodyLarge"
            color="questionsForm.formGrey"
          >
            {t(TranslationKeys.variety_planner_form_info_text_1)}
          </Typography>
          <Typography
            component="p"
            variant="appBodyLarge"
            color="questionsForm.formGrey"
            sx={{ mb: { md: 2 } }}
          >
            {t(TranslationKeys.variety_planner_form_info_text_2)}
          </Typography>
        </Box>
        <Button sx={styles.cleanUpBtn} onClick={onResetForm}>
          <Typography variant="appButtonText">
            {t(TranslationKeys.variety_planner_clear_filters_button_text)}
          </Typography>
        </Button>
      </Box>
      <Grid container spacing={1.75}>
        {simpleQuestions.map((question) => (
          <Grid item xs={12} md={3} key={question.id} sx={{ display: 'flex' }}>
            <AppCard>
              <Question
                question={question}
                value={formState[question.id]}
                onChange={onFormChange}
              />
            </AppCard>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={1.75} paddingTop={1.75}>
        {multipleChoiceQuestions.map((question) => (
          <Grid item xs={12} md={6} key={question.id} sx={{ display: 'flex' }}>
            <AppCard>
              <Question
                question={question}
                value={formState[question.id]}
                onChange={onFormChange}
              />
            </AppCard>
          </Grid>
        ))}
      </Grid>

      {questions.length > 4 && (
        <IconButton
          sx={styles.questionsToggleIconBtn}
          disableRipple
          onClick={handleToggleExpand}
        >
          {isExpanded ? (
            <RemoveCircleOutlineOutlinedIcon />
          ) : (
            <AddCircleOutlineOutlinedIcon />
          )}
          <Typography variant="appButtonText" sx={{ marginLeft: 0.5 }}>
            {isExpanded
              ? t(TranslationKeys.variety_planner_filters_see_less_btn_text)
              : t(TranslationKeys.variety_planner_filters_see_more_btn_text)}
          </Typography>
        </IconButton>
      )}
    </Box>
  );
};

export default memo(VpForm);
